import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "cc-root" }
const _hoisted_2 = {
  key: 0,
  class: "cc-loading"
}
const _hoisted_3 = { class: "cc-loader" }
const _hoisted_4 = { class: "cc-loading-bar" }
const _hoisted_5 = { class: "cc-loading-label cc-mono" }
const _hoisted_6 = { class: "cc-header" }
const _hoisted_7 = { class: "cc-brand" }
const _hoisted_8 = { class: "cc-brand-meta" }
const _hoisted_9 = { class: "cc-header-right" }
const _hoisted_10 = { class: "cc-balance" }
const _hoisted_11 = { class: "cc-balance-value cc-mono" }
const _hoisted_12 = ["aria-label", "title"]
const _hoisted_13 = {
  viewBox: "0 0 24 24",
  class: "cc-icon",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_14 = { class: "cc-main" }
const _hoisted_15 = { class: "cc-board" }
const _hoisted_16 = { class: "cc-confetti-layer" }
const _hoisted_17 = { class: "cc-chart-toggle" }
const _hoisted_18 = { class: "cc-side" }
const _hoisted_19 = { class: "card-slot cc-card" }
const _hoisted_20 = { class: "cc-chart" }
const _hoisted_21 = {
  viewBox: "0 0 150 240",
  preserveAspectRatio: "xMidYMid meet",
  class: "cc-chart-svg"
}
const _hoisted_22 = ["y1", "y2"]
const _hoisted_23 = ["y1", "y2"]
const _hoisted_24 = ["y1", "y2"]
const _hoisted_25 = ["y1", "y2"]
const _hoisted_26 = ["x1", "y1", "x2", "y2", "stroke"]
const _hoisted_27 = ["x", "y", "height", "fill"]
const _hoisted_28 = ["points", "stroke"]
const _hoisted_29 = ["cx", "cy", "fill"]
const _hoisted_30 = ["y1", "y2", "stroke"]
const _hoisted_31 = ["cy", "fill"]
const _hoisted_32 = {
  key: 2,
  x: "75",
  y: "112",
  "text-anchor": "middle",
  class: "chart-empty",
  fill: "#6b5b8a"
}
const _hoisted_33 = { class: "cc-side" }
const _hoisted_34 = { class: "card-slot cc-card" }
const _hoisted_35 = { class: "cc-status-overlay" }
const _hoisted_36 = {
  key: 1,
  class: "cc-cashout"
}
const _hoisted_37 = { class: "cc-cashout-title" }
const _hoisted_38 = { class: "cc-cashout-actions" }
const _hoisted_39 = ["disabled"]
const _hoisted_40 = { class: "cc-mono" }
const _hoisted_41 = ["disabled"]
const _hoisted_42 = { class: "cc-mono" }
const _hoisted_43 = { class: "cc-history" }
const _hoisted_44 = { class: "cc-panel" }
const _hoisted_45 = { class: "cc-field" }
const _hoisted_46 = { class: "cc-field-label" }
const _hoisted_47 = { class: "cc-chips" }
const _hoisted_48 = ["onClick"]
const _hoisted_49 = ["disabled"]
const _hoisted_50 = { class: "cc-bets-2" }
const _hoisted_51 = { class: "cc-bets-2" }
const _hoisted_52 = ["disabled"]

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { DisplayAmount, ParseAmount } from "stake-engine";
import type { Round } from "stake-engine";
import { useRgs } from "../composables/useRgs";
import { useSound } from "../composables/useSound";

type BetType = "bull" | "bear" | "doji" | "moon" | "crash";

// Play modes sent to the RGS. These must match the modes defined in the
// math model deployed for this game on Stake Engine.
const API_MULTIPLIER = 1_000_000;

// v3 payouts — must match games/candle_clash in the math SDK.
// Hold-to-cap distance ladder: the further the price finishes in your
// direction, the bigger the win.
const maxTick = 5; // per-tick decision cap
interface Particle {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  color: string;
  delay: number;
}
interface Candle {
  open: number;
  close: number;
  hi: number;
  lo: number;
}

type BookTickLabel =
  | "bullish"
  | "strongBuy"
  | "bearish"
  | "panicSell"
  | "volatilitySpike"
  | "moonHit"
  | "crashHit";

type DecisionAction = "hold" | "takeProfit" | "takeLoss";

type BookEvent =
  | { index: number; type: "roundStart" }
  | {
      index: number;
      type: "tick";
      tick: number;
      bullCard: number;
      bearCard: number;
      movement: number;
      priceIndex: number;
      label: BookTickLabel;
    }
  | {
      index: number;
      type: "decision";
      tick: number;
      action: DecisionAction;
      amount: number;
    }
  | { index: number; type: "setTotalWin"; amount: number }
  | { index: number; type: "finalWin"; amount: number };


export default /*@__PURE__*/_defineComponent({
  __name: 'CandleClash',
  setup(__props) {

const BET_MODE: Record<BetType, string> = {
  bull: "bull",
  bear: "bear",
  doji: "doji",
  moon: "moon",
  crash: "crash",
};

// RGS amounts are fixed-point integers: 1.00 == 1_000_000
const DISTANCE_LADDER: Record<number, number> = {
  1: 1.4,
  2: 1.8,
  3: 2.5,
  4: 3.5,
  5: 6.0,
};
// Take-profit ladder: lock in the current mark-to-market by favourable distance.
const TAKE_PROFIT_LADDER: Record<number, number> = {
  1: 1.3,
  2: 1.8,
  3: 2.6,
  4: 3.8,
};
// Take-loss ladder: cut losses for a partial refund that decays the further
// the position is underwater (adverse distance).
const TAKE_LOSS_REFUND: Record<number, number> = {
  1: 0.7,
  2: 0.5,
  3: 0.3,
  4: 0.1,
};
const PAYOUTS = {
  moon: 21,
  crash: 21,
  dojiExact: 9,
  dojiNear: 2,
};

// --- STAKE ENGINE (RGS) ---
const rgs = useRgs();
const isLive = rgs.isRgsMode;

// --- SOCIAL MODE WORDING ---
// Stake Engine requirement: when launched with social=true, restricted
// gambling phrases must be replaced (bet -> play, wager -> play amount,
// cash -> coins, ...). Real-money mode keeps the standard wording.
const isSocial = rgs.isSocialMode;
const words = isSocial
  ? {
      wagerAmount: "Play Amount",
      selectBet: "SELECT A PLAY",
      betVerb: "PLAY",
      betRejected: "PLAY REJECTED",
      cashedOut: "COLLECTED",
      insufficientFunds: "INSUFFICIENT COINS",
      strongBuy: "SURGE",
    }
  : {
      wagerAmount: "Wager Amount",
      selectBet: "SELECT A BET",
      betVerb: "BET",
      betRejected: "BET REJECTED",
      cashedOut: "CASHED OUT",
      insufficientFunds: "INSUFFICIENT FUNDS",
      strongBuy: "STRONG BUY",
    };

// --- SOUND (Web Audio synth, see useSound.ts) ---
const sound = useSound();
const { muted } = sound;

function toggleMute() {
  sound.toggleMute();
  sound.click();
}

// --- LOADING SCREEN ---
const showLoading = ref(true);
const loadingProgress = ref(0);
const loadingLabel = ref("BOOTING TERMINAL");
let loadingTimer: ReturnType<typeof setInterval> | null = null;

const LOADING_LABELS = [
  "BOOTING TERMINAL",
  "FETCHING MARKET DATA",
  "CALIBRATING VOLATILITY",
  "OPENING EXCHANGE",
];

function startLoading() {
  const startedAt = Date.now();
  let done = false;
  loadingTimer = setInterval(() => {
    const connecting = isLive && !rgs.ready.value && !rgs.initError.value;
    // Hold at 85% while waiting on the RGS handshake.
    const target = connecting ? 85 : 100;
    loadingProgress.value = Math.min(
      target,
      loadingProgress.value + 3 + Math.random() * 7
    );

    if (isLive && rgs.initError.value) {
      loadingLabel.value = "CONNECTION FAILED";
    } else {
      const idx = Math.min(
        LOADING_LABELS.length - 1,
        Math.floor(loadingProgress.value / 30)
      );
      loadingLabel.value = LOADING_LABELS[idx];
    }

    const minShown = Date.now() - startedAt >= 1400;
    const ready = !isLive || rgs.ready.value || !!rgs.initError.value;
    if (loadingProgress.value >= 100 && minShown && ready && !done) {
      done = true;
      if (loadingTimer) clearInterval(loadingTimer);
      loadingTimer = null;
      // Matches appReady timeline: progress completes, then overlay fades.
      setTimeout(() => (showLoading.value = false), 350);
    }
  }, 120);
}

onMounted(() => {
  if (isLive) rgs.init();
  startLoading();
  window.addEventListener("keydown", onSpacebar);
});
onUnmounted(() => {
  rgs.dispose();
  if (loadingTimer) clearInterval(loadingTimer);
  window.removeEventListener("keydown", onSpacebar);
});

// Approval requirement: spacebar maps to the bet button, unless the
// jurisdiction disables it.
function onSpacebar(e: KeyboardEvent) {
  if (e.code !== "Space" || e.repeat) return;
  if (rgs.jurisdictionFlags.value?.disabledSpacebar) return;
  if (showDecision.value) return;
  e.preventDefault();
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();
  startGame();
}

// --- GAME STATE ---
const demoBalance = ref(1000);
const wager = ref(100);
const currentBetType = ref<BetType | null>(null);
const isPlaying = ref(false);

const priceIndex = ref(5); // 0 to 10 (5 is Center/0)
const tickCount = ref(0);
const volatility = ref(1);
const moonHit = ref(false);
const crashHit = ref(false);

// --- UI STATE ---
const errorMessage = ref("");

// --- STAGE EFFECTS (shake / flash / confetti / final-tick tension) ---
const stageShake = ref(false);
const flashColor = ref<string | null>(null);

const particles = ref<Particle[]>([]);
let particleId = 0;

const isFinalTick = computed(
  () => isPlaying.value && tickCount.value >= maxTick
);

const FLASH = {
  moon: "rgba(177, 75, 255, 0.5)",
  crash: "rgba(255, 109, 0, 0.5)",
};
const WIN_COLORS = ["#ffd23f", "#00e676", "#ff2e97", "#00e5ff"];

let shakeTimer: ReturnType<typeof setTimeout> | null = null;
function shakeStage() {
  stageShake.value = false;
  // Force a reflow-free retrigger on the next frame.
  requestAnimationFrame(() => (stageShake.value = true));
  if (shakeTimer) clearTimeout(shakeTimer);
  shakeTimer = setTimeout(() => (stageShake.value = false), 450);
}

let flashTimer: ReturnType<typeof setTimeout> | null = null;
function flashStage(color: string) {
  flashColor.value = null;
  requestAnimationFrame(() => (flashColor.value = color));
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => (flashColor.value = null), 650);
}

function burstConfetti(colors: string[] = WIN_COLORS, count = 28) {
  const burst: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = 60 + Math.random() * 160;
    burst.push({
      id: particleId++,
      x: 46 + Math.random() * 8,
      y: 40 + Math.random() * 10,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist - 80,
      color: colors[i % colors.length],
      delay: Math.random() * 0.15,
    });
  }
  particles.value = burst;
  setTimeout(() => {
    if (particles.value === burst) particles.value = [];
  }, 1300);
}

// --- CANDLESTICK CHART ---
// Values are in "price" space: +5 (moon, top) .. -5 (crash, bottom).
// priceIndex 0..10 maps to value = 5 - priceIndex.
const candles = ref<Candle[]>([]);
// Chart rendering mode: candlesticks (default) or a close-price line.
const chartType = ref<"candles" | "line">("candles");
function setChartType(type: "candles" | "line") {
  if (chartType.value === type) return;
  chartType.value = type;
  sound.click();
}
const gridValues = [-4, -3, -2, -1, 1, 2, 3, 4];

const currentValue = computed(() => 5 - priceIndex.value);

const priceColor = computed(() => {
  const v = currentValue.value;
  if (v >= 5) return "#b14bff";
  if (v <= -5) return "#ff6d00";
  if (v > 0) return "#00e676";
  if (v < 0) return "#ff1744";
  return "#f5f0ff";
});

// viewBox is 150x240; value +5 -> y 20, value -5 -> y 220.
function yFor(v: number): number {
  return 120 - v * 20;
}

function candleX(i: number): number {
  const n = Math.max(candles.value.length, 3);
  const slot = 134 / n;
  return 8 + slot * i + slot / 2;
}

function candleColor(c: Candle): string {
  if (c.close > c.open) return "#00e676";
  if (c.close < c.open) return "#ff1744";
  return "#f5f0ff";
}

// Close-price polyline for the line chart: anchor at centre, then each close.
const linePoints = computed<string>(() => {
  if (candles.value.length === 0) return "";
  const pts = [`8,${yFor(0)}`];
  candles.value.forEach((c, i) => {
    pts.push(`${candleX(i)},${yFor(c.close)}`);
  });
  return pts.join(" ");
});

function bodyY(c: Candle): number {
  return yFor(Math.max(c.open, c.close));
}

function bodyHeight(c: Candle): number {
  return Math.max(4, Math.abs(c.open - c.close) * 20);
}

// Draw a candle for a tick and play the matching directional sound.
function pushCandle(prevIndex: number, newIndex: number) {
  const open = 5 - prevIndex;
  const close = 5 - newIndex;
  const up = close > open;
  const down = close < open;
  const wick = 0.15 + Math.random() * 0.35;
  const hi = Math.min(5, Math.max(open, close) + (up ? wick : wick * 0.5));
  const lo = Math.max(-5, Math.min(open, close) - (down ? wick : wick * 0.5));
  candles.value.push({ open, close, hi, lo });
  if (up) sound.tickUp();
  else if (down) sound.tickDown();
  else sound.tickFlat();
}

// Quick-pick chips. In live mode the operator dictates the valid bet
// levels, so each chip (and any custom amount) snaps to the nearest one.
const PREFERRED_WAGERS = [10, 50, 100, 500];

const allowedWagers = computed<number[]>(() => {
  if (isLive && rgs.config.value) {
    return rgs.config.value.betLevels.map((level) => ParseAmount(level));
  }
  return [];
});

const wagerOptions = computed<number[]>(() => {
  if (allowedWagers.value.length === 0) return PREFERRED_WAGERS;
  const snapped = PREFERRED_WAGERS.map((amount) => snapToBetLevel(amount));
  return [...new Set(snapped)];
});

function snapToBetLevel(amount: number): number {
  const levels = allowedWagers.value;
  if (levels.length === 0) return amount;
  return levels.reduce((best, level) =>
    Math.abs(level - amount) < Math.abs(best - amount) ? level : best
  );
}

const customWagerInput = ref("");

const isCustomWager = computed(() => !wagerOptions.value.includes(wager.value));

function applyCustomWager() {
  if (isPlaying.value) return;
  const value = Number.parseFloat(customWagerInput.value);
  if (!Number.isFinite(value) || value <= 0) {
    customWagerInput.value = "";
    return;
  }
  const snapped = snapToBetLevel(value);
  wager.value = snapped;
  if (snapped !== value) customWagerInput.value = String(snapped);
}

// Pick the operator's default bet level once authenticated.
watch(rgs.config, (config) => {
  if (config) wager.value = ParseAmount(config.defaultBetLevel);
});

const balanceDisplay = computed(() => {
  if (!isLive) return `$${demoBalance.value}`;
  return rgs.balance.value ? DisplayAmount(rgs.balance.value) : "—";
});

const canAfford = computed(() => {
  if (isLive) {
    return (rgs.balance.value?.amount ?? 0) >= wager.value * API_MULTIPLIER;
  }
  return demoBalance.value >= wager.value;
});
const bullCard = ref<string>("?");
const bearCard = ref<string>("?");
const bullBounce = ref(false);
const bearBounce = ref(false);
const statusText = ref("");
const statusColor = ref("cc-c-default");
const statusVisible = ref(false);
const history = ref<number[]>([]);
// Per-tick decision prompt state (Take Profit / Take Loss / Hold).
const showDecision = ref(false);
const profitAmount = ref(0); // guaranteed win if take profit (0 = not offered)
const lossRefund = ref(0); // partial refund if take loss (0 = not offered)
const hasPlayed = ref(false);

let statusTimer: ReturnType<typeof setTimeout> | null = null;

// --- BET BUTTON VISUALS ---
function betBtnClass(type: BetType): string {
  if (!currentBetType.value) return "";
  if (currentBetType.value === type) return "cc-bet-selected";
  return "cc-bet-dimmed";
}

// --- COMPUTED ACTION BUTTON ---
const actionEnabled = computed(() => {
  if (isPlaying.value || !currentBetType.value) return false;
  if (isLive && !rgs.ready.value) return false;
  return canAfford.value;
});

const actionLabel = computed(() => {
  if (isLive && rgs.initError.value) return "CONNECTION FAILED";
  if (isLive && !rgs.ready.value) return "CONNECTING...";
  if (isPlaying.value) return "MARKET OPEN";
  if (hasPlayed.value && !currentBetType.value) return "PLAY AGAIN";
  if (!currentBetType.value) return words.selectBet;
  if (!canAfford.value) return words.insufficientFunds;
  return `${words.betVerb} ${formatWager(
    wager.value
  )} ON ${currentBetType.value.toUpperCase()}`;
});

const betDisplay = computed(() => {
  if (isLive && rgs.initError.value) return rgs.initError.value;
  if (errorMessage.value) return errorMessage.value;
  if (isPlaying.value || !currentBetType.value || !canAfford.value) return "";
  return `Potential Win: up to ${formatWager(calculatePotentialWin())}`;
});

function formatWager(amount: number): string {
  const currency = rgs.balance.value?.currency;
  if (isLive && currency) {
    return DisplayAmount(
      { amount: Math.round(amount * API_MULTIPLIER), currency },
      { trimDecimalForIntegers: true }
    );
  }
  return `$${amount}`;
}

function calculatePotentialWin(): number {
  if (currentBetType.value === "doji") return wager.value * PAYOUTS.dojiExact;
  if (currentBetType.value === "moon" || currentBetType.value === "crash")
    return wager.value * PAYOUTS.moon;
  return wager.value * DISTANCE_LADDER[5];
}

// --- BETTING LOGIC ---
function setWager(amount: number) {
  if (isPlaying.value) return;
  wager.value = amount;
  customWagerInput.value = "";
  sound.chip();
}

function placeBet(type: BetType) {
  if (isPlaying.value) return;
  currentBetType.value = type;
  sound.click();
}

// --- GAME LOOP ---
async function startGame() {
  if (isPlaying.value || !actionEnabled.value) return;
  errorMessage.value = "";

  if (isLive) {
    await startServerRound();
    return;
  }

  isPlaying.value = true;
  demoBalance.value -= wager.value;
  resetBoard();
  sound.marketOpen();
  showStatus("MARKET OPEN", "cc-c-info");

  await wait(800);
  gameLoop();
}

function resetBoard() {
  candles.value = [];
  priceIndex.value = 5;
  tickCount.value = 0;
  volatility.value = 1;
  moonHit.value = false;
  crashHit.value = false;
  bullCard.value = "?";
  bearCard.value = "?";
}

// --- LIVE (STAKE ENGINE) ROUND ---
// The RGS decides the outcome when the bet is placed. With the candle_clash
// math model deployed, round.state carries the book's events for this round
// (roundStart / tick / cashOutOffer / setTotalWin / finalWin) and we replay
// them 1:1. If a server ever returns no events (e.g. a bare mock), we fall
// back to the old synthetic animation that lands on the win/lose verdict.

// Book event shapes — must match the math model (games/candle_clash).
// All book amounts are x100 bet multipliers (130 = 1.3x bet).
const TICK_LABEL_COLOR: Record<BookTickLabel, string> = {
  bullish: "cc-c-up",
  strongBuy: "cc-c-up",
  bearish: "cc-c-down",
  panicSell: "cc-c-down",
  volatilitySpike: "cc-c-moon",
  moonHit: "cc-c-moon",
  crashHit: "cc-c-crash",
};

function tickLabelText(event: Extract<BookEvent, { type: "tick" }>): string {
  switch (event.label) {
    case "strongBuy":
      return `${words.strongBuy} (+${Math.abs(event.movement)})`;
    case "panicSell":
      return `PANIC SELL (-${Math.abs(event.movement)})`;
    case "volatilitySpike":
      return "VOLATILITY SPIKE";
    case "moonHit":
      return "MOON HIT +5";
    case "crashHit":
      return "CRASH HIT −5";
    case "bullish":
      return "BULLISH";
    case "bearish":
      return "BEARISH";
  }
}

// Favourable distance for the current directional play: positive = in profit,
// negative = underwater (adverse distance).
function favorableDistance(): number {
  const v = 5 - priceIndex.value; // +5 (moon) .. -5 (crash)
  if (currentBetType.value === "bull") return v;
  if (currentBetType.value === "bear") return -v;
  return 0;
}

// Populate the Take Profit / Take Loss offer from the current board position.
function computeDecisionOffer() {
  const dist = favorableDistance();
  profitAmount.value =
    dist >= 1
      ? Math.floor(wager.value * TAKE_PROFIT_LADDER[Math.min(dist, 4)])
      : 0;
  lossRefund.value =
    dist <= -1
      ? Math.floor(wager.value * TAKE_LOSS_REFUND[Math.min(-dist, 4)])
      : 0;
}

async function replayBookEvents(events: BookEvent[]): Promise<void> {
  for (const event of events) {
    if (event.type === "tick") {
      tickCount.value = event.tick;
      displayCard("bull", event.bullCard);
      displayCard("bear", event.bearCard);
      await wait(600);

      const prevIndex = priceIndex.value;
      priceIndex.value = event.priceIndex;
      pushCandle(prevIndex, event.priceIndex);
      if (Math.abs(event.movement) >= 2) shakeStage();
      if (event.label === "moonHit") {
        moonHit.value = true;
        sound.moon();
        flashStage(FLASH.moon);
      }
      if (event.label === "crashHit") {
        crashHit.value = true;
        sound.crash();
        shakeStage();
        flashStage(FLASH.crash);
      }
      if (event.label === "volatilitySpike") sound.spike();

      showStatus(tickLabelText(event), TICK_LABEL_COLOR[event.label]);
      await wait(1500);
    } else if (event.type === "decision") {
      // The outcome is predetermined by the book — the prompt is
      // presentational; any click acknowledges it, then the book's action
      // is revealed.
      computeDecisionOffer();
      sound.offer();
      showDecision.value = true;
      await new Promise<void>((resolve) => {
        decisionResolver = resolve;
      });
      showDecision.value = false;

      if (event.action === "takeProfit") {
        sound.cashout();
        showStatus("PROFIT LOCKED", "cc-c-gold", 1500);
        await wait(1200);
      } else if (event.action === "takeLoss") {
        sound.click();
        showStatus("LOSS CUT", "cc-c-crash", 1500);
        await wait(1200);
      } else {
        showStatus("HOLDING...", "cc-c-info", 1000);
        await wait(900);
      }
    }
    // roundStart: board is already reset. setTotalWin/finalWin: the final
    // status is shown from round.payout in startServerRound.
  }
}

async function startServerRound() {
  const betType = currentBetType.value as BetType;
  isPlaying.value = true;
  resetBoard();
  sound.marketOpen();
  showStatus("MARKET OPEN", "cc-c-info");

  let round: Round;
  try {
    round = await rgs.play(
      Math.round(wager.value * API_MULTIPLIER),
      BET_MODE[betType]
    );
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : String(e);
    sound.error();
    showStatus(words.betRejected, "cc-c-down", 2000);
    isPlaying.value = false;
    return;
  }

  await wait(800);
  const events = Array.isArray(round.state) ? (round.state as BookEvent[]) : [];
  if (events.some((event) => event.type === "tick")) {
    await replayBookEvents(events);
  } else {
    // No book events in the response — synthetic fallback.
    await animateServerOutcome(betType, round);
  }

  try {
    if (rgs.roundActive.value) await rgs.endRound();
  } catch (e) {
    console.error("EndRound failed", e);
  }

  const payout = round.payout ?? 0;
  if (payout > 0) {
    sound.win();
    burstConfetti();
    showStatus(`WIN +${formatPayout(payout)}`, "cc-c-gold", 2000);
  } else {
    sound.lose();
    showStatus("LIQUIDATED", "cc-c-muted", 2000);
  }

  addToHistory(5 - priceIndex.value);
  finishRound();
}

function formatPayout(apiAmount: number): string {
  const currency = rgs.balance.value?.currency;
  if (currency) {
    return DisplayAmount(
      { amount: apiAmount, currency },
      { trimDecimalForIntegers: true }
    );
  }
  return `$${ParseAmount(apiAmount)}`;
}

async function animateServerOutcome(betType: BetType, round: Round) {
  const win = (round.payoutMultiplier ?? 0) > 0;
  const target = pickTargetIndex(betType, win);
  let current = 5;

  for (let tick = 1; tick <= maxTick; tick++) {
    tickCount.value = tick;
    const step = pickStep(current, target, maxTick - tick);
    const [bullVal, bearVal] = cardsForStep(step);
    displayCard("bull", bullVal);
    displayCard("bear", bearVal);
    await wait(600);

    const prevIndex = current;
    current += step;
    priceIndex.value = current;
    pushCandle(prevIndex, current);

    let statusMsg: string;
    let color: string;
    if (step <= -2) {
      statusMsg = `${words.strongBuy} (+${-step})`;
      color = "cc-c-up";
    } else if (step === -1) {
      statusMsg = "BULLISH";
      color = "cc-c-up";
    } else if (step >= 2) {
      statusMsg = `PANIC SELL (-${step})`;
      color = "cc-c-down";
    } else if (step === 1) {
      statusMsg = "BEARISH";
      color = "cc-c-down";
    } else {
      statusMsg = "SIDEWAYS CHOP";
      color = "cc-c-moon";
    }

    if (current === 0) {
      moonHit.value = true;
      statusMsg = "MOON HIT +5";
      sound.moon();
      flashStage(FLASH.moon);
    }
    if (current === 10) {
      crashHit.value = true;
      statusMsg = "CRASH HIT −5";
      sound.crash();
      shakeStage();
      flashStage(FLASH.crash);
    }

    showStatus(statusMsg, color);
    await wait(1500);
  }
}

// Final LED position consistent with the server's win/lose verdict.
// Remember: index 0 is the top of the strip (MOON), 10 the bottom (CRASH).
function pickTargetIndex(betType: BetType, win: boolean): number {
  switch (betType) {
    case "bull":
      return win ? randInt(1, 4) : randInt(5, 9);
    case "bear":
      return win ? randInt(6, 9) : randInt(1, 5);
    case "doji": {
      if (win) return 5;
      const offCenter = [1, 2, 3, 4, 6, 7, 8, 9];
      return offCenter[Math.floor(Math.random() * offCenter.length)];
    }
    case "moon":
      return win ? 0 : randInt(2, 9);
    case "crash":
      return win ? 10 : randInt(1, 8);
  }
}

// Random tick movement that stays on the board, keeps the extremes for the
// final tick, and can still reach the target within the remaining ticks.
function pickStep(
  current: number,
  target: number,
  ticksLeftAfter: number
): number {
  const candidates: number[] = [];
  for (let step = -2; step <= 2; step++) {
    const next = current + step;
    if (next < 0 || next > 10) continue;
    if (ticksLeftAfter > 0 && (next === 0 || next === 10)) continue;
    if (Math.abs(target - next) > 2 * ticksLeftAfter) continue;
    candidates.push(step);
  }
  return candidates[Math.floor(Math.random() * candidates.length)] ?? 0;
}

// Card faces that visually justify the movement of this tick.
function cardsForStep(step: number): [number, number] {
  if (step === 0) {
    const tie = randInt(2, 14);
    return [tie, tie];
  }
  const big = Math.abs(step) >= 2;
  const loser = big ? randInt(2, 10) : randInt(2, 9);
  const winner = big ? randInt(11, 14) : randInt(loser + 1, 10);
  return step < 0 ? [winner, loser] : [loser, winner];
}

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

async function gameLoop() {
  // Reached the 5-tick cap: settle whatever position stands.
  if (tickCount.value >= maxTick) {
    await settleRound();
    return;
  }

  tickCount.value++;

  // 1. Draw + animate cards
  const bullVal = getRandomCard();
  const bearVal = getRandomCard();
  displayCard("bull", bullVal);
  displayCard("bear", bearVal);
  await wait(600);

  // 2. Resolve the tick movement
  const prevIndex = priceIndex.value;
  let movement = 0;
  let statusMsg = "";
  let color = "cc-c-default";
  let baseStep = 1;

  if (bullVal > bearVal) {
    if (bullVal >= 11) baseStep = 2;
    const totalSteps = baseStep * volatility.value;
    movement = -totalSteps;
    statusMsg =
      totalSteps >= 2 ? `${words.strongBuy} (+${totalSteps})` : "BULLISH";
    color = "cc-c-up";
    volatility.value = 1;
  } else if (bearVal > bullVal) {
    if (bearVal >= 11) baseStep = 2;
    const totalSteps = baseStep * volatility.value;
    movement = totalSteps;
    statusMsg = totalSteps >= 2 ? `PANIC SELL (-${totalSteps})` : "BEARISH";
    color = "cc-c-down";
    volatility.value = 1;
  } else {
    // Tie -> volatility spike (next move doubled)
    movement = 0;
    volatility.value = 2;
    statusMsg = "VOLATILITY SPIKE";
    color = "cc-c-moon";
    sound.spike();
  }

  priceIndex.value += movement;
  if (Math.abs(movement) >= 2) shakeStage();

  // 3. Clamp + extreme hits (instant end)
  let ended = false;
  if (priceIndex.value <= 0) {
    priceIndex.value = 0;
    moonHit.value = true;
    statusMsg = "MOON HIT +5";
    color = "cc-c-moon";
    sound.moon();
    flashStage(FLASH.moon);
    ended = true;
  } else if (priceIndex.value >= 10) {
    priceIndex.value = 10;
    crashHit.value = true;
    statusMsg = "CRASH HIT −5";
    color = "cc-c-crash";
    sound.crash();
    shakeStage();
    flashStage(FLASH.crash);
    ended = true;
  }

  pushCandle(prevIndex, priceIndex.value);
  showStatus(statusMsg, color);
  await wait(1500);

  if (ended) {
    await settleRound();
    return;
  }

  // 4. Decision point: BULL/BEAR positions choose every tick until the cap.
  const directional =
    currentBetType.value === "bull" || currentBetType.value === "bear";
  if (directional && tickCount.value < maxTick) {
    promptDecision();
    return;
  }

  gameLoop();
}

// --- PER-TICK DECISION (Take Profit / Take Loss / Hold) ---
// In live mode the modal resolves a pending promise inside replayBookEvents and
// the predetermined book action plays out. In local mode the choice is real.
let decisionResolver: (() => void) | null = null;

function resolveLiveDecision(): boolean {
  if (!decisionResolver) return false;
  const resolve = decisionResolver;
  decisionResolver = null;
  showDecision.value = false;
  resolve();
  return true;
}

function promptDecision() {
  computeDecisionOffer();
  sound.offer();
  showDecision.value = true;
}

function chooseProfit() {
  if (profitAmount.value <= 0) return;
  sound.cashout();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  const win = profitAmount.value;
  demoBalance.value += win;
  burstConfetti(["#ffd23f", "#00e5ff", "#ff2e97"], 16);
  showStatus(`PROFIT LOCKED +$${win}`, "cc-c-gold", 2500);
  addToHistory(5 - priceIndex.value);
  finishRound();
}

function chooseLoss() {
  if (lossRefund.value <= 0) return;
  sound.click();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  const refund = lossRefund.value;
  demoBalance.value += refund;
  showStatus(`LOSS CUT +$${refund}`, "cc-c-crash", 2500);
  addToHistory(5 - priceIndex.value);
  finishRound();
}

async function chooseHold() {
  sound.click();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  showStatus("HOLDING...", "cc-c-info", 900);
  await wait(900);
  gameLoop();
}

// --- SETTLEMENT (cap reached or extreme hit) ---
async function settleRound() {
  await wait(300);
  const betType = currentBetType.value;
  const finalValue = 5 - priceIndex.value;
  let winAmount = 0;

  if (betType === "moon" && moonHit.value)
    winAmount = wager.value * PAYOUTS.moon;
  else if (betType === "crash" && crashHit.value)
    winAmount = wager.value * PAYOUTS.crash;
  else if (betType === "bull" && finalValue > 0)
    winAmount = Math.floor(
      wager.value * DISTANCE_LADDER[Math.min(finalValue, 5)]
    );
  else if (betType === "bear" && finalValue < 0)
    winAmount = Math.floor(
      wager.value * DISTANCE_LADDER[Math.min(-finalValue, 5)]
    );
  else if (betType === "doji" && finalValue === 0)
    winAmount = wager.value * PAYOUTS.dojiExact;
  else if (betType === "doji" && Math.abs(finalValue) === 1)
    winAmount = wager.value * PAYOUTS.dojiNear;

  if (winAmount > 0) {
    demoBalance.value += winAmount;
    sound.win();
    burstConfetti();
    showStatus(`WIN +$${winAmount}`, "cc-c-gold", 2000);
  } else {
    sound.lose();
    showStatus("LIQUIDATED", "cc-c-muted", 2000);
  }

  addToHistory(finalValue);
  finishRound();
}

function finishRound() {
  isPlaying.value = false;
  hasPlayed.value = true;
  currentBetType.value = null;
}

// --- HELPERS ---
function getRandomCard(): number {
  return Math.floor(Math.random() * 13) + 2;
}

function displayCard(side: "bull" | "bear", val: number) {
  let displayVal = String(val);
  if (val === 11) displayVal = "J";
  if (val === 12) displayVal = "Q";
  if (val === 13) displayVal = "K";
  if (val === 14) displayVal = "A";

  if (side === "bull") {
    // One flip sound per tick (bull + bear always flip together).
    sound.cardFlip();
    bullCard.value = displayVal;
    bullBounce.value = true;
    setTimeout(() => (bullBounce.value = false), 500);
  } else {
    bearCard.value = displayVal;
    bearBounce.value = true;
    setTimeout(() => (bearBounce.value = false), 500);
  }
}

function showStatus(text: string, color: string, duration = 1000) {
  statusText.value = text;
  statusColor.value = color;
  statusVisible.value = true;
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = setTimeout(() => {
    statusVisible.value = false;
  }, duration);
}

function addToHistory(val: number) {
  history.value.unshift(val);
  if (history.value.length > 20) history.value.pop();
}

function historyPillClass(val: number): string {
  const base = "cc-hist cc-mono";
  if (val > 0) return `${base} is-up`;
  if (val < 0) return `${base} is-down`;
  return `${base} is-flat`;
}

function historyPillText(val: number): string {
  if (val > 0) return `+${val}`;
  if (val < 0) return String(val);
  return "0";
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, { name: "loading-fade" }, {
      default: _withCtx(() => [
        (showLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                  return _createElementVNode("div", {
                    key: n,
                    class: "loader-candle",
                    style: _normalizeStyle({ animationDelay: `${n * 0.13}s` })
                  }, null, 4)
                }), 64))
              ]),
              _cache[8] || (_cache[8] = _createElementVNode("h1", { class: "cc-loading-title" }, [
                _createTextVNode(" Candle"),
                _createElementVNode("span", { class: "cc-accent" }, "Clash")
              ], -1)),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", {
                  class: "cc-loading-fill",
                  style: _normalizeStyle({ width: `${loadingProgress.value}%` })
                }, null, 4)
              ]),
              _createElementVNode("p", _hoisted_5, _toDisplayString(loadingLabel.value), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("header", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _cache[13] || (_cache[13] = _createStaticVNode("<div class=\"cc-brand-badge\" data-v-0b5314b1><svg viewBox=\"0 0 24 24\" class=\"cc-badge-svg\" fill=\"none\" data-v-0b5314b1><rect x=\"4\" y=\"10\" width=\"3\" height=\"8\" rx=\"1\" fill=\"#00e676\" data-v-0b5314b1></rect><line x1=\"5.5\" y1=\"7\" x2=\"5.5\" y2=\"20\" stroke=\"#00e676\" stroke-width=\"1.2\" data-v-0b5314b1></line><rect x=\"10.5\" y=\"6\" width=\"3\" height=\"7\" rx=\"1\" fill=\"#ff1744\" data-v-0b5314b1></rect><line x1=\"12\" y1=\"4\" x2=\"12\" y2=\"15\" stroke=\"#ff1744\" stroke-width=\"1.2\" data-v-0b5314b1></line><rect x=\"17\" y=\"9\" width=\"3\" height=\"6\" rx=\"1\" fill=\"#00e5ff\" data-v-0b5314b1></rect><line x1=\"18.5\" y1=\"6\" x2=\"18.5\" y2=\"17\" stroke=\"#00e5ff\" stroke-width=\"1.2\" data-v-0b5314b1></line></svg></div>", 1)),
        _createElementVNode("div", _hoisted_8, [
          _cache[10] || (_cache[10] = _createElementVNode("h1", { class: "cc-brand-name" }, [
            _createTextVNode(" Candle"),
            _createElementVNode("span", { class: "cc-accent" }, "Clash")
          ], -1)),
          _cache[11] || (_cache[11] = _createElementVNode("span", { class: "cc-brand-dot" }, "/", -1)),
          _cache[12] || (_cache[12] = _createElementVNode("span", { class: "cc-brand-sub" }, "Volatility", -1)),
          _createElementVNode("span", {
            class: _normalizeClass(['cc-status-chip', _unref(isLive) ? 'is-live' : 'is-local'])
          }, [
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "cc-status-dot" }, null, -1)),
            _createTextVNode(" " + _toDisplayString(_unref(isLive) ? "LIVE" : "LOCAL"), 1)
          ], 2)
        ])
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _cache[14] || (_cache[14] = _createElementVNode("p", { class: "cc-balance-label" }, "Balance", -1)),
          _createElementVNode("p", _hoisted_11, _toDisplayString(balanceDisplay.value), 1)
        ]),
        _createElementVNode("button", {
          onClick: toggleMute,
          "aria-label": _unref(muted) ? 'Unmute sound' : 'Mute sound',
          title: _unref(muted) ? 'Unmute sound' : 'Mute sound',
          class: "cc-icon-btn"
        }, [
          (_openBlock(), _createElementBlock("svg", _hoisted_13, [
            _cache[19] || (_cache[19] = _createElementVNode("path", {
              d: "M3 9v6h4l5 4V5L7 9H3z",
              fill: "currentColor"
            }, null, -1)),
            (_unref(muted))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _cache[15] || (_cache[15] = _createElementVNode("line", {
                    x1: "16",
                    y1: "9",
                    x2: "22",
                    y2: "15"
                  }, null, -1)),
                  _cache[16] || (_cache[16] = _createElementVNode("line", {
                    x1: "22",
                    y1: "9",
                    x2: "16",
                    y2: "15"
                  }, null, -1))
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _cache[17] || (_cache[17] = _createElementVNode("path", { d: "M15.5 8.5a5 5 0 0 1 0 7" }, null, -1)),
                  _cache[18] || (_cache[18] = _createElementVNode("path", { d: "M18.5 6a9 9 0 0 1 0 12" }, null, -1))
                ], 64))
          ]))
        ], 8, _hoisted_12)
      ])
    ]),
    _createElementVNode("main", _hoisted_14, [
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", {
          class: _normalizeClass([
            'cc-stage',
            stageShake.value && 'stage-shake',
            isFinalTick.value && 'stage-final',
          ])
        }, [
          (flashColor.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "cc-flash stage-flash",
                style: _normalizeStyle({ background: flashColor.value })
              }, null, 4))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_16, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(particles.value, (p) => {
              return (_openBlock(), _createElementBlock("div", {
                key: p.id,
                class: "confetti",
                style: _normalizeStyle({
                left: `${p.x}%`,
                top: `${p.y}%`,
                background: p.color,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
                animationDelay: `${p.delay}s`,
              })
              }, null, 4))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass([
                'cc-chart-toggle-btn',
                chartType.value === 'candles' && 'is-active',
              ]),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (setChartType('candles'))),
              title: "Candlestick view",
              "aria-label": "Candlestick view"
            }, [...(_cache[20] || (_cache[20] = [
              _createStaticVNode("<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" data-v-0b5314b1><line x1=\"7\" y1=\"3\" x2=\"7\" y2=\"21\" data-v-0b5314b1></line><rect x=\"4.5\" y=\"7\" width=\"5\" height=\"8\" rx=\"1\" fill=\"currentColor\" stroke=\"none\" data-v-0b5314b1></rect><line x1=\"17\" y1=\"3\" x2=\"17\" y2=\"21\" data-v-0b5314b1></line><rect x=\"14.5\" y=\"9\" width=\"5\" height=\"7\" rx=\"1\" fill=\"currentColor\" stroke=\"none\" data-v-0b5314b1></rect></svg>", 1)
            ]))], 2),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass([
                'cc-chart-toggle-btn',
                chartType.value === 'line' && 'is-active',
              ]),
              onClick: _cache[1] || (_cache[1] = ($event: any) => (setChartType('line'))),
              title: "Line view",
              "aria-label": "Line view"
            }, [...(_cache[21] || (_cache[21] = [
              _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              }, [
                _createElementVNode("polyline", { points: "3,15 9,9 13,13 21,5" })
              ], -1)
            ]))], 2)
          ]),
          _createElementVNode("div", _hoisted_18, [
            _cache[22] || (_cache[22] = _createElementVNode("div", { class: "cc-side-label is-bull" }, [
              _createElementVNode("span", { class: "cc-mono" }, "▲"),
              _createTextVNode(" BULLS ")
            ], -1)),
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("div", {
                class: _normalizeClass([
                  'cc-card-face cc-mono',
                  bullBounce.value && 'card-flip',
                  bullCard.value === '?' ? 'is-empty' : 'is-bull',
                ])
              }, _toDisplayString(bullCard.value), 3)
            ])
          ]),
          _createElementVNode("div", _hoisted_20, [
            (_openBlock(), _createElementBlock("svg", _hoisted_21, [
              _cache[23] || (_cache[23] = _createElementVNode("text", {
                x: "75",
                y: "10",
                "text-anchor": "middle",
                class: "chart-label",
                fill: "#b14bff"
              }, " ▲ MOON +5 ", -1)),
              _cache[24] || (_cache[24] = _createElementVNode("text", {
                x: "75",
                y: "236",
                "text-anchor": "middle",
                class: "chart-label",
                fill: "#ff6d00"
              }, " ▼ CRASH −5 ", -1)),
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(gridValues, (v) => {
                return _createElementVNode("line", {
                  key: `grid-${v}`,
                  x1: "8",
                  y1: yFor(v),
                  x2: "142",
                  y2: yFor(v),
                  stroke: "#3a2a52",
                  "stroke-width": "1",
                  opacity: "0.7"
                }, null, 8, _hoisted_22)
              }), 64)),
              _createElementVNode("line", {
                x1: "8",
                y1: yFor(0),
                x2: "142",
                y2: yFor(0),
                stroke: "#a596c4",
                "stroke-width": "1",
                "stroke-dasharray": "3 3",
                opacity: "0.55"
              }, null, 8, _hoisted_23),
              _createElementVNode("line", {
                x1: "8",
                y1: yFor(5),
                x2: "142",
                y2: yFor(5),
                stroke: "#b14bff",
                "stroke-width": "1.5",
                opacity: "0.7"
              }, null, 8, _hoisted_24),
              _createElementVNode("line", {
                x1: "8",
                y1: yFor(-5),
                x2: "142",
                y2: yFor(-5),
                stroke: "#ff6d00",
                "stroke-width": "1.5",
                opacity: "0.7"
              }, null, 8, _hoisted_25),
              (chartType.value === 'candles')
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(candles.value, (c, i) => {
                    return (_openBlock(), _createElementBlock("g", {
                      key: `candle-${i}`,
                      class: "candle"
                    }, [
                      _createElementVNode("line", {
                        x1: candleX(i),
                        y1: yFor(c.hi),
                        x2: candleX(i),
                        y2: yFor(c.lo),
                        stroke: candleColor(c),
                        "stroke-width": "2",
                        "stroke-linecap": "round"
                      }, null, 8, _hoisted_26),
                      _createElementVNode("rect", {
                        x: candleX(i) - 7,
                        y: bodyY(c),
                        width: "14",
                        height: bodyHeight(c),
                        rx: "2",
                        fill: candleColor(c)
                      }, null, 8, _hoisted_27)
                    ]))
                  }), 128))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (linePoints.value)
                      ? (_openBlock(), _createElementBlock("polyline", {
                          key: 0,
                          points: linePoints.value,
                          fill: "none",
                          stroke: priceColor.value,
                          "stroke-width": "2",
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          class: "cc-line"
                        }, null, 8, _hoisted_28))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candles.value, (c, i) => {
                      return (_openBlock(), _createElementBlock("circle", {
                        key: `pt-${i}`,
                        cx: candleX(i),
                        cy: yFor(c.close),
                        r: "2.4",
                        fill: candleColor(c)
                      }, null, 8, _hoisted_29))
                    }), 128))
                  ], 64)),
              _createElementVNode("line", {
                x1: "8",
                y1: yFor(currentValue.value),
                x2: "142",
                y2: yFor(currentValue.value),
                stroke: priceColor.value,
                "stroke-width": "1",
                "stroke-dasharray": "4 3",
                opacity: "0.9"
              }, null, 8, _hoisted_30),
              _createElementVNode("circle", {
                cx: "136",
                cy: yFor(currentValue.value),
                r: "3.5",
                fill: priceColor.value,
                class: "price-dot"
              }, null, 8, _hoisted_31),
              (candles.value.length === 0 && !isPlaying.value)
                ? (_openBlock(), _createElementBlock("text", _hoisted_32, " AWAITING OPEN "))
                : _createCommentVNode("", true)
            ]))
          ]),
          _createElementVNode("div", _hoisted_33, [
            _cache[25] || (_cache[25] = _createElementVNode("div", { class: "cc-side-label is-bear" }, [
              _createElementVNode("span", { class: "cc-mono" }, "▼"),
              _createTextVNode(" BEARS ")
            ], -1)),
            _createElementVNode("div", _hoisted_34, [
              _createElementVNode("div", {
                class: _normalizeClass([
                  'cc-card-face cc-mono',
                  bearBounce.value && 'card-flip',
                  bearCard.value === '?' ? 'is-empty' : 'is-bear',
                ])
              }, _toDisplayString(bearCard.value), 3)
            ])
          ]),
          _createElementVNode("div", _hoisted_35, [
            _createElementVNode("div", {
              class: _normalizeClass([
                'cc-status-text',
                statusColor.value,
                statusVisible.value ? 'is-visible' : 'is-hidden',
              ])
            }, _toDisplayString(statusText.value), 3)
          ]),
          (showDecision.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                _createElementVNode("div", _hoisted_37, " Tick " + _toDisplayString(tickCount.value) + " / " + _toDisplayString(maxTick) + " — Your Call ", 1),
                _createElementVNode("div", _hoisted_38, [
                  _createElementVNode("button", {
                    onClick: chooseProfit,
                    disabled: profitAmount.value <= 0,
                    class: "cc-btn cc-btn-profit"
                  }, [
                    _cache[26] || (_cache[26] = _createTextVNode(" TAKE PROFIT ", -1)),
                    _createElementVNode("span", _hoisted_40, _toDisplayString(profitAmount.value > 0 ? `+$${profitAmount.value}` : "—"), 1)
                  ], 8, _hoisted_39),
                  _createElementVNode("button", {
                    onClick: chooseHold,
                    class: "cc-btn cc-btn-hold"
                  }, " HOLD "),
                  _createElementVNode("button", {
                    onClick: chooseLoss,
                    disabled: lossRefund.value <= 0,
                    class: "cc-btn cc-btn-loss"
                  }, [
                    _cache[27] || (_cache[27] = _createTextVNode(" TAKE LOSS ", -1)),
                    _createElementVNode("span", _hoisted_42, _toDisplayString(lossRefund.value > 0 ? `+$${lossRefund.value}` : "—"), 1)
                  ], 8, _hoisted_41)
                ]),
                _cache[28] || (_cache[28] = _createElementVNode("p", { class: "cc-cashout-note" }, " Lock the guaranteed value, cut losses for a partial refund, or hold for the next tick. ", -1))
              ]))
            : _createCommentVNode("", true)
        ], 2),
        _createElementVNode("div", _hoisted_43, [
          _cache[29] || (_cache[29] = _createElementVNode("span", { class: "cc-history-label" }, "History", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(history.value, (val, i) => {
            return (_openBlock(), _createElementBlock("div", {
              key: i,
              class: _normalizeClass(historyPillClass(val))
            }, _toDisplayString(historyPillText(val)), 3))
          }), 128))
        ])
      ]),
      _createElementVNode("div", _hoisted_44, [
        _createElementVNode("div", _hoisted_45, [
          _createElementVNode("label", _hoisted_46, _toDisplayString(_unref(words).wagerAmount), 1),
          _createElementVNode("div", _hoisted_47, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(wagerOptions.value, (amount) => {
              return (_openBlock(), _createElementBlock("button", {
                key: amount,
                onClick: ($event: any) => (setWager(amount)),
                class: _normalizeClass(['cc-chip cc-mono', wager.value === amount && 'is-selected'])
              }, _toDisplayString(formatWager(amount)), 11, _hoisted_48))
            }), 128))
          ]),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((customWagerInput).value = $event)),
            type: "number",
            min: "0",
            inputmode: "decimal",
            placeholder: "Custom amount",
            disabled: isPlaying.value,
            onKeyup: _withKeys(applyCustomWager, ["enter"]),
            onBlur: applyCustomWager,
            class: _normalizeClass(['cc-input cc-mono', isCustomWager.value && 'is-active'])
          }, null, 42, _hoisted_49), [
            [_vModelText, customWagerInput.value]
          ])
        ]),
        _cache[35] || (_cache[35] = _createElementVNode("div", { class: "cc-divider" }, null, -1)),
        _createElementVNode("div", _hoisted_50, [
          _createElementVNode("button", {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (placeBet('bull'))),
            class: _normalizeClass(['cc-bet is-bull', betBtnClass('bull')])
          }, [...(_cache[30] || (_cache[30] = [
            _createElementVNode("div", { class: "cc-bet-mult cc-mono" }, "x1.4–x6", -1),
            _createElementVNode("span", { class: "cc-bet-arrow" }, "▲", -1),
            _createElementVNode("span", { class: "cc-bet-name" }, "BULL", -1),
            _createElementVNode("span", { class: "cc-bet-desc" }, "Long the market", -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[4] || (_cache[4] = ($event: any) => (placeBet('bear'))),
            class: _normalizeClass(['cc-bet is-bear', betBtnClass('bear')])
          }, [...(_cache[31] || (_cache[31] = [
            _createElementVNode("div", { class: "cc-bet-mult cc-mono" }, "x1.4–x6", -1),
            _createElementVNode("span", { class: "cc-bet-arrow" }, "▼", -1),
            _createElementVNode("span", { class: "cc-bet-name" }, "BEAR", -1),
            _createElementVNode("span", { class: "cc-bet-desc" }, "Short the market", -1)
          ]))], 2)
        ]),
        _createElementVNode("button", {
          onClick: _cache[5] || (_cache[5] = ($event: any) => (placeBet('doji'))),
          class: _normalizeClass(['cc-bet-row', betBtnClass('doji')])
        }, [...(_cache[32] || (_cache[32] = [
          _createStaticVNode("<div class=\"cc-bet-row-left\" data-v-0b5314b1><span class=\"cc-bet-row-icon\" data-v-0b5314b1>—</span><div class=\"cc-bet-row-meta\" data-v-0b5314b1><span class=\"cc-bet-row-name\" data-v-0b5314b1>THE DOJI</span><span class=\"cc-bet-row-desc\" data-v-0b5314b1>Exactly 0 • near miss (±1) pays x2</span></div></div><span class=\"cc-bet-row-pay cc-mono\" data-v-0b5314b1>x9.0</span>", 2)
        ]))], 2),
        _createElementVNode("div", _hoisted_51, [
          _createElementVNode("button", {
            onClick: _cache[6] || (_cache[6] = ($event: any) => (placeBet('moon'))),
            class: _normalizeClass(['cc-bet-mini is-moon', betBtnClass('moon')])
          }, [...(_cache[33] || (_cache[33] = [
            _createElementVNode("div", { class: "cc-bet-mini-sub" }, "HITS TOP (+5)", -1),
            _createElementVNode("div", { class: "cc-bet-mini-name" }, [
              _createTextVNode(" MOON "),
              _createElementVNode("span", { class: "cc-mono" }, "x21")
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[7] || (_cache[7] = ($event: any) => (placeBet('crash'))),
            class: _normalizeClass(['cc-bet-mini is-crash', betBtnClass('crash')])
          }, [...(_cache[34] || (_cache[34] = [
            _createElementVNode("div", { class: "cc-bet-mini-sub" }, "HITS BOT (-5)", -1),
            _createElementVNode("div", { class: "cc-bet-mini-name" }, [
              _createTextVNode(" CRASH "),
              _createElementVNode("span", { class: "cc-mono" }, "x21")
            ], -1)
          ]))], 2)
        ]),
        _createElementVNode("button", {
          onClick: startGame,
          disabled: !actionEnabled.value,
          class: _normalizeClass(['cc-action', actionEnabled.value ? 'is-ready' : 'is-disabled'])
        }, _toDisplayString(actionLabel.value), 11, _hoisted_52),
        _createElementVNode("div", {
          class: _normalizeClass([
            'cc-betinfo',
            errorMessage.value || (_unref(isLive) && _unref(rgs).initError.value) ? 'is-error' : '',
          ])
        }, _toDisplayString(betDisplay.value), 3)
      ])
    ]),
    _createElementVNode("footer", { class: "cc-footer" }, [
      _cache[36] || (_cache[36] = _createElementVNode("p", null, " PAYS: Bull/Bear hold ladder x1.4–x6 • Doji x9 (near miss x2) • Moon/Crash x21 ", -1)),
      _createElementVNode("p", { class: "cc-footer-sub" }, " Every tick you choose: TAKE PROFIT (lock the mark), TAKE LOSS (partial refund), or HOLD — up to " + _toDisplayString(maxTick) + " ticks "),
      _cache[37] || (_cache[37] = _createElementVNode("p", { class: "cc-legal" }, " Malfunction voids all wins and plays. A consistent internet connection is required. In the event of a disconnection, reload the game to finish any uncompleted rounds. The expected return is calculated over many plays. The game display is not representative of any physical device and is for illustrative purposes only. Winnings are settled according to the amount received from the Remote Game Server and not from events within the web browser. TM and © 2026 Stake Engine. ", -1))
    ])
  ]))
}
}

})